// ============================================================================
// ClientForgotPage — route /me/forgot (PUBLIC, client realm). A client requests
// a reset by email OR by the portal name they log in with (POST
// /api/client/forgot via clientApi.forgot). NON-ENUMERATING: the endpoint always
// returns a generic success regardless of whether the email/name matched, so
// this page ALWAYS shows the same "if that account exists, we sent a link"
// confirmation — it never reveals whether the input matched. A reset link is
// only emailed on a real match WITH an email on file; the client is reminded to
// contact their trainer if they don't get one.
//
// Mobile-first Pulse, EN + 繁中 via i18n keys only. Reuses ClientAuthBrand.
// ============================================================================
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Button, Card, TextField } from "../../components";
import { ApiError, clientApi } from "../../api";
import { ClientLanguageToggle } from "../ClientLanguageToggle";
import { ClientAuthBrand } from "./ClientAuthBrand";
import { CLIENT_LOGIN_PATH } from "../routes";

export function ClientForgotPage() {
  const { t } = useTranslation("clientPortal");

  const [value, setValue] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    // The single field accepts either an email or the portal name; send it as
    // both so the backend can match on whichever it is (it prefers email).
    const trimmed = value.trim();
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    try {
      await clientApi.forgot(
        looksLikeEmail ? { email: trimmed } : { name: trimmed },
      );
      // ALWAYS the same generic confirmation — never reveal if it matched.
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("forgot.rateLimited"));
      } else {
        setError(t("forgot.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <ClientAuthBrand />
        <ClientLanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        {sent ? (
          <Card className="space-y-5">
            <span className="grid h-12 w-12 place-items-center rounded-md bg-lime-soft text-lime-600">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
                <path
                  d="M4 6.5 12 12l8-5.5M4 6h16v12H4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <div>
              <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body">
                {t("forgot.sent.title")}
              </h1>
              <p className="mt-1.5 text-body-soft">{t("forgot.sent.blurb")}</p>
            </div>
            <p className="rounded-md bg-surface-sunken px-4 py-3 text-sm text-body-soft">
              {t("forgot.sent.hint")}
            </p>
            <Link to={CLIENT_LOGIN_PATH}>
              <Button block size="lg" variant="secondary">
                {t("forgot.backToLogin")}
              </Button>
            </Link>
          </Card>
        ) : (
          <Card className="space-y-5">
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
                {t("forgot.kicker")}
              </p>
              <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
                {t("forgot.title")}
              </h1>
              <p className="mt-1.5 text-body-soft">{t("forgot.blurb")}</p>
            </div>

            {error && <Banner tone="danger" title={error} />}

            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <TextField
                label={t("forgot.field")}
                autoComplete="username"
                placeholder={t("forgot.fieldPlaceholder")}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(null);
                }}
                hint={t("forgot.fieldHint")}
                required
                autoFocus
                disabled={submitting}
              />
              <Button type="submit" block size="lg" disabled={submitting}>
                {submitting ? t("forgot.sending") : t("forgot.submit")}
              </Button>
            </form>

            <p className="text-center text-sm text-body-soft">
              {t("forgot.remembered")}{" "}
              <Link
                to={CLIENT_LOGIN_PATH}
                className="font-bold text-coral hover:underline"
              >
                {t("forgot.backToLogin")}
              </Link>
            </p>
          </Card>
        )}
      </main>
    </div>
  );
}
